/**
 * Pure helpers for the smart-categorisation feature in the settlement view.
 *
 * `tokenize` and the Jaccard threshold mirror the sidecar's reconciliation
 * logic (`apps/sidecar/extraction/reconciliation.py:_description_similarity`)
 * so suggestions feel consistent with how the extractor groups transactions.
 *
 * Nothing here touches React, the API, or DB — descriptions arrive already
 * decrypted on the client via `settlement.get`, so detection runs locally
 * against the in-memory expenses array.
 */

const TOKEN_RE = /[a-z0-9æøå]+/g;
const MIN_MEANINGFUL_TOKEN_LENGTH = 3;

export interface SimilarityExpense {
  id: string;
  description: string;
  categoryId: string | null;
}

export function tokenize(s: string): string[] {
  return s.toLowerCase().match(TOKEN_RE) ?? [];
}

export function normalizeDescription(s: string): string {
  return s.trim().replace(/\s+/g, " ").toLowerCase();
}

export function jaccard(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let intersection = 0;
  for (const token of a) {
    if (b.has(token)) intersection++;
  }
  const union = a.size + b.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

export function commonMeaningfulTokens(...sets: Set<string>[]): string[] {
  const [first, ...rest] = sets;
  if (!first) return [];
  const out: string[] = [];
  for (const token of first) {
    if (token.length < MIN_MEANINGFUL_TOKEN_LENGTH) continue;
    if (rest.every((s) => s.has(token))) out.push(token);
  }
  return out;
}

/**
 * Other expenses with the exact same (normalized) description as `target`
 * but a different category. Used by the bulk-update dialog.
 */
export function findExactMatchSiblings<T extends SimilarityExpense>(
  target: SimilarityExpense,
  others: ReadonlyArray<T>,
  targetCategoryId: string,
): T[] {
  const targetNorm = normalizeDescription(target.description);
  return others.filter(
    (x) =>
      x.id !== target.id &&
      x.categoryId !== targetCategoryId &&
      normalizeDescription(x.description) === targetNorm,
  );
}

export interface SimilarSibling<T> {
  expense: T;
  score: number;
}

/**
 * Other expenses that ALREADY share `targetCategoryId` and whose descriptions
 * are similar (Jaccard ≥ threshold) to `target`. Used by the pattern-suggestion
 * dialog — at least one such sibling means the user has manually placed two
 * similar items in the same category, which is the signal to propose a
 * persistent pattern.
 */
export function findSimilarSiblings<T extends SimilarityExpense>(
  target: SimilarityExpense,
  others: ReadonlyArray<T>,
  targetCategoryId: string,
  threshold = 0.5,
): SimilarSibling<T>[] {
  const targetTokens = new Set(tokenize(target.description));
  if (targetTokens.size === 0) return [];
  const out: SimilarSibling<T>[] = [];
  for (const x of others) {
    if (x.id === target.id) continue;
    if (x.categoryId !== targetCategoryId) continue;
    const score = jaccard(targetTokens, new Set(tokenize(x.description)));
    if (score >= threshold) out.push({ expense: x, score });
  }
  return out;
}
